/**
 * B76C · 2b — the SHEET SIDE of lane B's seam.
 *
 * `contexts/AthleteMembershipSheetContext` (lane B) says exactly what this
 * file has to be: "Lane C mounts `<AthleteMembershipSheetProvider
 * value={{ open }}>` above the routes (App.tsx, alongside the other app-level
 * mounts) and renders the sheet itself." So this is a PROVIDER that wraps the
 * routes, holds the open state, and renders `<AthleteMembershipSheet>`.
 *
 * Four boundary CTAs call `useOpenAthleteMembershipSheet(origin)` (1a rail
 * footer, 1b chat card, 1c athlete-surface boundary, 4b Settings card). None
 * of them knows this component exists, and this component knows nothing about
 * them beyond the `origin` they name — which is the whole point of the seam.
 *
 * `open` is `useCallback`-stable because lane B's provider memoizes on
 * `value.open`; an unstable identity would re-render every boundary surface in
 * the tree on every render of this host.
 *
 * Signed-out is inert. Both rails require a session (StoreKit posts its
 * receipt to the server, Stripe checkout is an authed POST), so a signed-out
 * open would render a purchase surface that cannot transact.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import {
  AthleteMembershipSheetProvider,
  type AthleteMembershipOrigin,
} from '../../contexts/AthleteMembershipSheetContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AthleteMembershipSheet } from './AthleteMembershipSheet';

export function AthleteMembershipHost({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  // `null` = closed. The origin is retained (rather than a bare boolean) so a
  // later attribution pass has it without another round of lane coordination.
  const [origin, setOrigin] = useState<AthleteMembershipOrigin | null>(null);

  const open = useCallback((from: AthleteMembershipOrigin) => setOrigin(from), []);
  const api = useMemo(() => ({ open }), [open]);

  // A sign-out while the sheet is open closes it rather than leaving a
  // purchase surface over a signed-out app.
  useEffect(() => {
    if (!user) setOrigin(null);
  }, [user]);

  return (
    <AthleteMembershipSheetProvider value={api}>
      {children}
      {origin !== null && user && (
        <AthleteMembershipSheet
          variant={isDesktop ? 'modal' : 'sheet'}
          onClose={() => setOrigin(null)}
        />
      )}
    </AthleteMembershipSheetProvider>
  );
}

export default AthleteMembershipHost;
